import "./styles.scss";
import agrisenseLogo from "../../assets/img/v5-agrisense.png";

const Icon = ({ name, size = 22 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="M13 6l6 6-6 6" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    crop: (
      <>
        <path d="M12 21V10" />
        <path d="M12 10C8 10 5 7.8 5 4c4 0 7 2.2 7 6Z" />
        <path d="M12 13c4 0 7-2.2 7-6-4 0-7 2.2-7 6Z" />
      </>
    ),
    map: (
      <>
        <path d="m9 18-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" />
        <path d="M9 3v15M15 6v15" />
      </>
    ),
    brain: (
      <>
        <path d="M8.5 14.5A3.5 3.5 0 0 1 5 11V9a4 4 0 0 1 6.7-3" />
        <path d="M15.5 14.5A3.5 3.5 0 0 0 19 11V9a4 4 0 0 0-6.7-3" />
        <path d="M8 16a4 4 0 0 0 8 0" />
        <path d="M12 5v14M8 9h3M13 9h3M8 13h3M13 13h3" />
      </>
    ),
    decision: (
      <>
        <path d="M4 12h5l2-7 3 14 2-7h4" />
        <path d="M4 20h16" />
      </>
    ),
    stages: (
      <>
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
        <path d="M7 12h3M14 12h3" />
        <path d="M12 4v3M12 17v3" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    sensor: (
      <>
        <rect x="8" y="3" width="8" height="14" rx="2" />
        <path d="M12 17v4M9 21h6M12 7v5" />
      </>
    ),
    camera: (
      <>
        <path d="M4 7h3l2-2h6l2 2h3v12H4Z" />
        <circle cx="12" cy="13" r="4" />
      </>
    ),
    gps: (
      <>
        <path d="M12 22s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
  };

  return <svg {...common}>{paths[name]}</svg>;
};

const reasons = [
  ["crop", "Know If Your Field Is Ready", "Choose a crop and crop period, then check if field conditions are suitable before planting."],
  ["map", "Monitor Multiple Fields", "Track plots separately, such as North Field, Lot 2, Greenhouse A, or any saved field location."],
  ["brain", "AI Crop Guidance", "Analyze soil readings, GPS, crop stage, photos, and farmer notes for practical recommendations."],
  ["decision", "Simple Farmer Decisions", "Show risk clearly and let farmers decide whether to prepare first, plant now, continue, or pause."],
  ["stages", "Stage-by-Stage Support", "Guide each crop period from land preparation through maturity and harvest."],
  ["bell", "Follow-Up Reminders", "Help farmers stay on schedule with checks and reminders for the next crop stage."],
  ["sensor", "Soil Sensor Integration", "Support USB-C readings for moisture, temperature, pH, EC, nitrogen, phosphorus, and potassium."],
  ["camera", "Photo-Based Crop Checks", "Capture field or crop photos and optimize them before AI analysis."],
  ["gps", "Field Map View", "Plot saved readings on a map so farmers can see exactly where each field check was taken."],
];

const stages = [
  "Land Preparation",
  "Planting",
  "Establishment",
  "Vegetative Growth",
  "Flowering / Reproductive",
  "Fruiting / Grain Filling",
  "Maturity / Harvest",
];

const readings = ["Moisture", "Temperature", "pH", "EC", "Nitrogen", "Phosphorus", "Potassium"];

const benefits = [
  "Better planting decisions",
  "Easier crop-period tracking",
  "Practical AI recommendations",
  "Field-specific monitoring",
  "Follow-up checks until harvest",
  "Support for the next crop cycle",
];

function AgriMockup() {
  return (
    <div className="agriMockup" aria-label="AgriSense Mobile crop monitoring preview">
      <div className="fieldBoard">
        <div className="boardTop">
          <span></span>
          <b>Crop Readiness</b>
          <small>North Field</small>
        </div>
        <div className="readinessScore">
          <div>
            <small>Selected Crop</small>
            <strong>Tomato</strong>
            <em>Dry season cycle</em>
          </div>
          <div className="scoreRing"><b>86%</b><span>Ready</span></div>
        </div>
        <div className="sensorGrid">
          <div><small>Soil Moisture</small><strong>62%</strong></div>
          <div><small>pH</small><strong>6.4</strong></div>
          <div><small>NPK</small><strong>Good</strong></div>
          <div><small>Risk</small><strong>Low</strong></div>
        </div>
        <div className="cropTrend">
          <svg viewBox="0 0 360 130" preserveAspectRatio="none">
            <path className="trendFill" d="M10 100 L62 74 L114 82 L166 42 L218 58 L270 30 L350 48 L350 122 L10 122 Z" />
            <path className="trendLine" d="M10 100 L62 74 L114 82 L166 42 L218 58 L270 30 L350 48" />
            <g>
              <circle cx="62" cy="74" r="5" />
              <circle cx="166" cy="42" r="5" />
              <circle cx="270" cy="30" r="5" />
              <circle cx="350" cy="48" r="5" />
            </g>
          </svg>
          <div><span>Prep</span><span>Plant</span><span>Grow</span><span>Flower</span><span>Harvest</span></div>
        </div>
      </div>

      <div className="farmerPhone">
        <div className="phoneTop">
          <img src={agrisenseLogo} alt="" />
          <span>AgriSense</span>
        </div>
        <div className="fieldPhoto">
          <i></i>
          <strong>Field Check</strong>
        </div>
        <div className="aiCard">
          <small>AI Guidance</small>
          <b>Planting is suitable. Check irrigation again in 3 days.</b>
        </div>
        <div className="decisionRow">
          <span>Prepare</span>
          <span className="active">Plant Now</span>
        </div>
      </div>

      <div className="mapCard">
        <Icon name="map" size={24} />
        <span>5 field readings mapped</span>
      </div>
      <div className="sensorCard">
        <Icon name="sensor" size={24} />
        <span>USB-C sensor synced</span>
      </div>
    </div>
  );
}

export default function AgrisenseMobile() {
  return (
    <main id="agrisenseMobilePage">
      <section className="agriHero">
        <div className="shell heroGrid">
          <div className="heroCopy">
            <span className="eyebrow">AgriSense Mobile</span>
            <div className="titleLockup">
              <img src={agrisenseLogo} alt="AgriSense Mobile logo" />
              <h1>AgriSense Mobile</h1>
            </div>
            <h2>Smart crop-period <span className="heroBreak">monitoring for farmers.</span></h2>
            <p>
              <span>AgriSense Mobile helps farmers decide if a field is ready.</span>
              <span>For a selected crop, farmers can monitor each stage.</span>
            </p>
            <p>
              <span>The app gives simple AI guidance until harvest.</span>
              <span>It helps prepare for the next crop period.</span>
            </p>
            <p className="heroQuestion">
              <span>“I know what I want to plant,</span>
              <span>and I know where I want to plant.</span>
              <span>Is this location good for this crop?”</span>
            </p>
            <div className="heroActions">
              <a className="button" href="#request-demo">Request a Demo <Icon name="arrow" size={17} /></a>
              <a className="button button--outline" href="#features">View Features</a>
            </div>
          </div>
          <AgriMockup />
        </div>
      </section>

      <section className="agriSummary">
        <div className="shell summaryCard">
          <div>
            <span>Short Description</span>
            <p>
              AgriSense Mobile is a crop-period assistant that combines soil
              readings, GPS, photos, and AI guidance to help farmers decide when
              and where to plant, monitor crop stages, and prepare for the next
              harvest cycle.
            </p>
          </div>
          <a href="#request-demo">Plan the next crop cycle <Icon name="arrow" size={16} /></a>
        </div>
      </section>

      <section className="whyChoose shell">
        <div className="sectionHeading">
          <span>Why Choose AgriSense Mobile?</span>
          <h2>Clear field decisions from crop choice to harvest.</h2>
        </div>
        <div className="reasonGrid">
          {reasons.map(([icon, title, text]) => (
            <article key={title}>
              <div><Icon name={icon} /></div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="stageSection" id="features">
        <div className="shell stageGrid">
          <div className="stageIntro">
            <span>Stage-by-Stage Support</span>
            <h2>Monitor every crop period with practical next steps.</h2>
            <p>
              For each stage, AgriSense shows what to prepare, what to expect,
              and when to check again.
            </p>
          </div>
          <div className="stageTrack">
            {stages.map((stage, index) => (
              <div key={stage}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{stage}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="fieldTools shell">
        <div className="toolPanel sensorPanel">
          <div className="sectionHeading left">
            <span>Soil Sensor Integration</span>
            <h2>Capture key readings from the field</h2>
          </div>
          <div className="readingGrid">
            {readings.map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>
        <div className="toolPanel">
          <div className="sectionHeading left">
            <span>Photo + Map Checks</span>
            <h2>Connect field evidence to each recommendation</h2>
          </div>
          <p>
            Farmers can take crop photos directly from the camera, optimize them
            before AI analysis, and view saved readings on a field map.
          </p>
        </div>
      </section>

      <section className="workflowBand">
        <div className="shell workflowGrid">
          {[
            ["crop", "Select", "Choose the crop and crop period for the field."],
            ["sensor", "Read", "Collect soil, GPS, notes, and photo evidence."],
            ["brain", "Guide", "Review AI recommendations and risk details."],
            ["bell", "Follow Up", "Set reminders until harvest and the next cycle."],
          ].map(([icon, title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <Icon name={icon} />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="farmerFit shell">
        <div className="fitPanel">
          <div className="sectionHeading left">
            <span>Who Is It For?</span>
            <h2>Built for farmers who want simple planting confidence</h2>
          </div>
          <p>
            AgriSense Mobile is built for farmers who already know what they
            want to plant and where they want to plant, but need a practical way
            to confirm if the location is good for that crop.
          </p>
        </div>
        <div className="fitPanel fitPanel--dark">
          <div className="sectionHeading left">
            <span>Main Benefits</span>
            <h2>Field-specific support from preparation to harvest</h2>
          </div>
          <div className="benefitList">
            {benefits.map((item) => <span key={item}><Icon name="check" size={16} />{item}</span>)}
          </div>
        </div>
      </section>

      <section className="finalCta" id="request-demo">
        <div className="shell ctaInner">
          <div>
            <span>Call to Action</span>
            <h2>Help farmers make better planting decisions with AgriSense Mobile.</h2>
            <p>Contact us today to request a demo or learn how field monitoring can support your crop cycle.</p>
          </div>
          <a className="button button--light" href="mailto:contact@brvtechnologysg.com">Contact Us Today <Icon name="arrow" size={16} /></a>
        </div>
      </section>
    </main>
  );
}
